/** 配送ステータス */
export enum ShipmentStatus {
  Creating = 'Creating',
  Create = 'Create',
  NotReady = 'NotReady',
  Ready = 'Ready',
  InProgress = 'InProgress',
  AtStop = 'AtStop',
  AtDestination = 'AtDestination',
  Completed = 'Completed', // 過去の配送履歴にも対応するためにキーを残しておく
  Deleted = 'Deleted',
  Interrupted = 'Interrupted',
}

/** 配送設定一覧のフィルタ用ステータス */
export type ShipmentStatusFilterKey = 'NotReady' | 'Ready' | 'InProgress';

/** 配送ステータスの表示ラベル */
export const ShipmentStatusDisplayLabel: Record<ShipmentStatus, string> = {
  Creating: '作成中',
  Create: '作成済',
  NotReady: '出発準備未完',
  Ready: '出発準備済',
  InProgress: '配送中',
  AtStop: '経由地到着',
  AtDestination: '到着地到着',
  Completed: '完了',
  Deleted: '完了',
  Interrupted: '中断',
};

/** 配送設定一覧の配送ステータス表示ラベル */
export const ShipmentStatusFilterLabelMap: Record<
  ShipmentStatusFilterKey,
  string
> = {
  NotReady: '準備未完',
  Ready: '準備完了',
  InProgress: '配送中',
};

/** 配送設定一覧のフィルタごとに対象とする配送ステータス */
export const ShipmentStatusFilterGroups: Record<
  ShipmentStatusFilterKey,
  ShipmentStatus[]
> = {
  NotReady: [ShipmentStatus.NotReady],
  Ready: [ShipmentStatus.Ready],
  InProgress: [
    ShipmentStatus.InProgress,
    ShipmentStatus.AtStop,
    ShipmentStatus.AtDestination,
  ],
};

/**
 * 文字列から配送ステータスに変換する
 *
 * @param value 値
 * @returns 配送ステータス。不適切な場合、undefinedとなる。
 */
export const convertToShipmentStatus = (
  value: string,
): ShipmentStatus | undefined => {
  return ShipmentStatus[value as keyof typeof ShipmentStatus];
};

/** 配送設定一覧のフィルタキーへ変換する */
export const getShipmentStatusFilterKey = (
  value?: string,
): ShipmentStatusFilterKey | undefined => {
  const shipmentStatus = value ? convertToShipmentStatus(value) : undefined;
  if (!shipmentStatus) {
    return undefined;
  }

  return (Object.keys(ShipmentStatusFilterGroups) as ShipmentStatusFilterKey[])
    .find((filterKey) =>
      ShipmentStatusFilterGroups[filterKey].includes(shipmentStatus),
    );
};

/** 配送ステータスが配送設定一覧のフィルタ条件に含まれるか判定する */
export const matchesShipmentStatusFilter = (
  filterKey: ShipmentStatusFilterKey,
  value?: string,
): boolean => {
  const shipmentStatus = value ? convertToShipmentStatus(value) : undefined;
  return shipmentStatus
    ? ShipmentStatusFilterGroups[filterKey].includes(shipmentStatus)
    : false;
};

/**
 * 配送前かどうか
 *
 * @param status 配送ステータス
 * @returns Trueなら配送前
 */
export const isBeforeShipping = (status: ShipmentStatus): boolean => {
  return ['Creating', 'Create', 'NotReady', 'Ready'].includes(status);
};
